import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Clock, MapPin, Calendar, Building2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { usePageMeta } from '@/hooks/usePageMeta';

const BrowseProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    function: '',
    location_type: '',
    search: '',
  });

  usePageMeta({
    title: 'Browse Opportunities | Talent Collective',
    description: 'Explore meaningful projects with mission-driven organizations in education, healthcare, environment, and women\'s empowerment.'
  });

  useEffect(() => {
    fetchProjects();
  }, [filters.function, filters.location_type]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.function) params.append('function', filters.function);
      if (filters.location_type) params.append('location_type', filters.location_type);
      
      const response = await axios.get(`${API}/projects?${params.toString()}`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  const filteredProjects = projects.filter(project => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return project.title.toLowerCase().includes(searchLower) || 
             project.description.toLowerCase().includes(searchLower) ||
             project.company_name?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const functionColors = {
    product: 'bg-teal-100 text-teal-700',
    design: 'bg-violet-100 text-violet-700',
    engineering: 'bg-blue-100 text-blue-700',
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="browse-projects-page">
      <Header />

      <div className="container-custom px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Browse Opportunities</h1>
          <p className="text-sm sm:text-base text-slate-600">Find your next meaningful opportunity.</p>
        </div>

        <Card className="p-3 sm:p-4 mb-6 sm:mb-8" data-testid="project-filters">
          <div className="flex flex-col gap-3 sm:gap-4">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search projects, organizations..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </form>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={filters.function} onValueChange={(v) => setFilters({ ...filters, function: v })}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-function">
                <SelectValue placeholder="Function" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.location_type} onValueChange={(v) => setFilters({ ...filters, location_type: v })}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-location">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
                <SelectItem value="onsite">On-site</SelectItem>
              </SelectContent>
            </Select>

            {(filters.function || filters.location_type || filters.search) && (
              <Button 
                variant="ghost" 
                onClick={() => setFilters({ function: '', location_type: '', search: '' })}
                data-testid="clear-filters"
              >
                Clear
              </Button>
            )}
            </div>
          </div>
        </Card>

        <div className="mb-4">
          <p className="text-sm text-slate-500" data-testid="results-count">
            {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} found.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2 mb-6"></div>
                <div className="h-20 bg-slate-200 rounded mb-4"></div>
                <div className="flex gap-2">
                  <div className="h-6 bg-slate-200 rounded w-16"></div>
                  <div className="h-6 bg-slate-200 rounded w-20"></div>
                </div>
              </Card>
            ))}
          </div>
        ) : filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id}>
                <Card className="p-6 card-hover cursor-pointer h-full flex flex-col" data-testid={`project-card-${project.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="heading-4 mb-1 truncate">{project.title}</h3>
                      <p className="text-sm text-slate-500 flex items-center">
                        <Building2 className="h-3.5 w-3.5 mr-1" />
                        {project.company_name || 'Organization'}
                      </p>
                    </div>
                    {project.is_featured && (
                      <span className="badge-accent text-xs ml-2">Featured</span>
                    )}
                  </div>
                  
                  <p className="text-slate-600 text-sm line-clamp-3 mb-4 flex-1">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-slate-100">
                    <span className={`${functionColors[project.function]} px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize`}>
                      {capitalize(project.function)}
                    </span>
                    <span className="badge-neutral flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {project.hours_per_week}h/week
                    </span>
                    <span className="badge-neutral flex items-center capitalize">
                      <MapPin className="h-3 w-3 mr-1" />
                      {capitalize(project.location_type)}
                    </span>
                    <span className="badge-neutral flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {project.duration}
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <div className="text-slate-400 mb-4">
              <Search className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="heading-4 mb-2">No projects found.</h3>
            <p className="text-slate-500 mb-4">
              Try adjusting your filters or check back later for new opportunities.
            </p>
            <Button onClick={() => setFilters({ function: '', location_type: '', search: '' })} data-testid="reset-filters">
              Reset Filters
            </Button>
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BrowseProjects;
